import type { PublicGovernmentResponse } from '@govinity/validation/public';

/**
 * A top-level category the portal search can surface results for:
 * - `topics` — legislative items (Topics nav)
 * - `documents` — meeting-published public documents, e.g. agenda packages and
 *   minutes (Meetings nav); these are the primary content without a dedicated
 *   search path otherwise. Topics already have their own tab.
 * - `meetings` — meeting transcript search (Meetings nav + feature flag)
 */
export type SearchCategory = 'topics' | 'documents' | 'meetings';

/**
 * Resolves which search categories a portal exposes, in display order.
 *
 * - `topics` — available when the Topics nav feature is enabled.
 * - `documents` — available when the Meetings nav feature is enabled. Public
 *   documents are primarily meeting-sourced content (agenda packages, minutes);
 *   topic documents are already reachable via the Topics tab.
 * - `meetings` — available when Meetings nav is enabled.
 *
 * @param government - The public government whose portal config gates the categories.
 * @returns The ordered list of available search categories.
 */
export function getAvailableSearchCategories(
  government: PublicGovernmentResponse
): SearchCategory[] {
  const hidden = government.customizations?.navHiddenElements;
  const categories: SearchCategory[] = [];

  if (!hidden?.topics) categories.push('topics');
  if (!hidden?.meetings) categories.push('documents', 'meetings');

  return categories;
}
